import type { ComponentType } from "react";
import { hydrateRoot } from "react-dom/client";
import type { DehydratedState } from "@tanstack/react-query";
import { config, initConfig } from "@borealis/huck/client";
import { loadAndActivateLocale, defaultLocale } from "./lib/i18n";
import { getQueryClient } from "./lib/queryClient";
import { Shell } from "./components/Shell";
import { views, type ViewKey } from "./views";

// Dev inlines each page's CSS into the document in definePageRoute (no import
// here — that would double-apply and flash). Prod bundles all CSS into one
// assets/style.css (cssCodeSplit:false) that Eik links render-blocking, so pull
// every route's view.css (each @imports its components' CSS) into that bundle.
// import.meta.glob derives the set from the filesystem, so a new route's CSS is
// never silently missing from prod; in dev this branch is compiled away.
if (!import.meta.env.DEV) {
	await Promise.all(Object.values(import.meta.glob("./views/*/view.css")).map((load) => load()));
}

const appElement = document.getElementById("app")!;

// Read the server-serialized { view, props } (data-config attribute on #app) so
// the client hydrates the same view with the same data the server rendered.
initConfig(appElement);
const view = config.view as ViewKey;
// A migrated route serializes its React Query cache under `dehydratedState`; split
// it off the view props and hand it to the shell's HydrationBoundary.
const { dehydratedState, ...props } = (config.props ?? {}) as {
	dehydratedState?: DehydratedState;
} & Record<string, unknown>;

// Fail loudly with a clear diagnostic if the serialized view key is missing or
// unknown, rather than the opaque "views[view] is not a function" we'd hit below.
if (!(view in views)) {
	throw new Error(`Cannot hydrate: unknown view "${String(view)}" in serialized config`);
}

const locale = document.documentElement.lang || defaultLocale;
await loadAndActivateLocale(locale);

// Only the current page's view chunk is fetched.
const { default: View } = await views[view]();
const Component = View as ComponentType<Record<string, unknown>>;

hydrateRoot(
	appElement,
	<Shell queryClient={getQueryClient()} dehydratedState={dehydratedState}>
		<Component {...props} />
	</Shell>,
);
